/** @format */

import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbDate, NgbDateStruct, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Profile, Configuration, blankConfiguration, ManageProfile } from 'src/app/models/profile';
import { getPayPeriods, getProfile, getTeamProfile } from 'src/app/store';
import {
	ApplicationState,
	GetTeamProfilePicsBulk,
	PayPeriod,
	SaveMemberProfile,
	TeamMemberProfile,
	UpdateMemberConfig,
	UpdateMemberProfile,
	User,
} from 'src/app/store/application';
import * as HelperFunctions from 'src/assets/helper-functions/helper-functions';

@Component({
    selector: 'app-team-profiles',
    templateUrl: './team-profiles.component.html',
    styleUrls: ['./team-profiles.component.scss'],
    standalone: false
})
export class TeamProfilesComponent implements OnInit {
	private ngUnsubscribe: Subject<void> = new Subject();

	@ViewChild('configModal') configModal: any;
	configModalRef!: NgbModalRef;
	isUpdateModal: boolean = false;

	profile$: Observable<Profile>;

	managedUsers: ManageProfile[] = [];

	teamProfile$: Observable<TeamMemberProfile[]>;
	teamProfile: TeamMemberProfile[] = [];

	payPeriods$: Observable<PayPeriod[]>;
	payPeriods!: PayPeriod[];
	payPeriodStartDates: Date[] = [];

	users: User[] = [];
	selectedUser: User = HelperFunctions.getBlankUser();

	userConfigurations: Configuration[] = [];
	selectedConfiguration: Configuration = blankConfiguration;

	selectedStartDate: Date = new Date();
	ngbStartDate: NgbDate = new NgbDate(2022, 1, 1);

	oldCdo: any = null;
	oldAdjHrsDay: any = null;
	oldAdjHrs: any = null;

	memberForm: UntypedFormGroup = new UntypedFormGroup({
		startDate: new UntypedFormControl(null, Validators.required),
		employmentStatus: new UntypedFormControl(-1, Validators.required),
		hoursPerPP: new UntypedFormControl('', [Validators.pattern('^[0-9]+(.?[0-9]+)?$'), Validators.required]),
		regDayHrs: new UntypedFormControl('', [Validators.pattern('^[0-9]+(.?[0-9]+)?$'), Validators.required]),
		adjHoursDay: new UntypedFormControl(-1, Validators.required),
		adjHours: new UntypedFormControl('', [Validators.pattern('^[0-9]+(.?[0-9]+)?$'), Validators.required]),
		cdoDay: new UntypedFormControl(-1, Validators.required),
		unionCode: new UntypedFormControl(-1, Validators.required),
	});

	oneWeek: number[] = Array(7)
		.fill(0)
		.map((x, i) => i + 1);

	empStatus = [
		{ desc: 'Regular full-time', code: 'RFT' },
		{ desc: 'Regular part-time', code: 'RPT' },
		{ desc: 'Temporary full-time', code: 'TFT' },
		{ desc: 'Auxilary', code: 'AUX' },
	];

	get hoursPerPP() {
		return this.memberForm.get('hoursPerPP');
	}
	get regDayHrs() {
		return this.memberForm.get('regDayHrs');
	}
	get adjHours() {
		return this.memberForm.get('adjHours');
	}
	get employmentStatus() {
		return this.memberForm.get('employmentStatus');
	}
	get adjHoursDay() {
		return this.memberForm.get('adjHoursDay');
	}
	get cdoDay() {
		return this.memberForm.get('cdoDay');
	}
	get unionCode() {
		return this.memberForm.get('unionCode');
	}

	constructor(private store: Store<ApplicationState>, private modalService: NgbModal) {
		this.profile$ = this.store.select(getProfile);
		this.teamProfile$ = this.store.select(getTeamProfile);
		this.payPeriods$ = this.store.select(getPayPeriods);
	}

	ngOnInit(): void {
		this.profile$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((profile) => {
			let isManager = profile.user.isManager;
			let isTimekeeper = profile.user.isTimekeeper;
			let isTimekeeperAndManager = isManager && isTimekeeper;

			let timekeeperScopeUsers = [...profile.timekeeperDelegated, ...profile.timekeeperManagedUsers];
			let managerScopeUsers = [...profile.manage, ...profile.delegated, ...profile.managedGoneFromDNV];

			if (isTimekeeper && !isTimekeeperAndManager) {
				// user is a timekeeper
				this.managedUsers = [...timekeeperScopeUsers];
			} else if (isManager && !isTimekeeperAndManager) {
				// user is a manager
				this.managedUsers = [...managerScopeUsers];
			} else {
				// user is both a manager and timekeeper
				let allUsers = [...timekeeperScopeUsers];
				allUsers.forEach((mu) => {
					let alreadyInList = allUsers.some((amu) => amu.user.id === mu.user.id);
					if (!alreadyInList) {
						allUsers.push(mu);
					}
				});

				this.managedUsers = allUsers;
			}

			this.users = [...this.managedUsers.map((managedUser) => managedUser.user)].sort((a: User, b: User) => {
				let aName = `${a.firstName} ${a.lastName}`;
				let bName = `${b.firstName} ${b.lastName}`;

				if (aName < bName) return -1;
				if (aName > bName) return 1;
				return 0;
			});

			let upns = this.users.map((user) => user.upn);
			this.store.dispatch(GetTeamProfilePicsBulk({ upns }));

			if (this.selectedUser.id !== -1) {
				let configs = this.managedUsers.find((managedUser) => managedUser.user.id === this.selectedUser.id)?.configurations || [];
				this.userConfigurations = HelperFunctions.sortConfigurations(configs);
			}
		});

		this.teamProfile$?.pipe(takeUntil(this.ngUnsubscribe)).subscribe((team) => (this.teamProfile = team));
		this.payPeriods$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payPeriods) => {
			this.payPeriodStartDates = payPeriods.map((pp) => new Date(pp.startDate));
			this.payPeriods = payPeriods;
		});
	}

	getUserName = (user: User) => `${user.firstName} ${user.lastName}`;

	getProfilePicture(user: User) {
		const member = this.teamProfile.find((teamMember) => HelperFunctions.compareEmail(teamMember.email, user.email));
		if (member) return member.profilePic;
		return null;
	}

	isDateDisabled = (date: NgbDateStruct) => {
		let calendarDate = HelperFunctions.getBeginningOfDayDate(new Date(date.year, date.month - 1, date.day));
		return !this.payPeriodStartDates.some((startDate) => startDate.valueOf() === calendarDate.valueOf());
	};

	shouldDarkenEntryBG(date: NgbDate) {
		const payPeriod = this.payPeriods.findIndex((d) => this.isInPP(date, new Date(d.startDate), new Date(d.endDate)));
		if (payPeriod > -1) {
			return payPeriod % 2 === 0;
		}
		return false;
	}

	isInPP(targetDate: NgbDate, start: Date, end: Date) {
		const ngbStart = new NgbDate(start.getFullYear(), start.getMonth() + 1, start.getDate());
		const ngbEnd = new NgbDate(end.getFullYear(), end.getMonth() + 1, end.getDate());
		if (targetDate.equals(ngbStart) || targetDate.equals(ngbEnd) || (targetDate.after(ngbStart) && targetDate.before(ngbEnd))) {
			return true;
		}
		return false;
	}

	getActiveStatus(config: Configuration) {
		let configuration = HelperFunctions.configurationOn(this.userConfigurations, new Date());
		return config.id === configuration.id ? 'Active' : 'Inactive';
	}

	getEmploymentStatus(statusCode: string) {
		return this.empStatus.find((empStat) => empStat.code === statusCode)?.desc || '';
	}

	viewProfileDetails(user: User) {
		if (user.id === this.selectedUser.id) {
			this.selectedUser = HelperFunctions.getBlankUser();
			this.userConfigurations = [];
			return;
		}

		let configs = this.managedUsers.find((managedUser) => managedUser.user.id === user.id)?.configurations || [];
		this.userConfigurations = HelperFunctions.sortConfigurations(configs);
		this.selectedUser = user;
	}

	onAddRow() {
		this.isUpdateModal = false;
		this.openConfigModal();
	}

	openConfigModal() {
		let currentDate = new Date();
		let currentPayPeriod = this.payPeriods.find((pp) =>
			HelperFunctions.getDateIsWithinBounds(new Date(pp.startDate), new Date(pp.endDate), currentDate)
		);

		let nextPayPeriod = this.payPeriods.find((pp) =>
			HelperFunctions.getDateIsWithinBounds(
				new Date(pp.startDate),
				new Date(pp.endDate),
				HelperFunctions.getFutureDate(currentPayPeriod!.endDate, 1)
			)
		);

		let startDate = new Date(this.userConfigurations.length > 0 ? nextPayPeriod?.startDate || new Date() : new Date('2020/01/01'));
		let year = startDate.getFullYear();
		let month = startDate.getMonth() + 1;
		let date = startDate.getDate();

		this.ngbStartDate = new NgbDate(year, month, date);
		this.selectedStartDate = startDate;

		this.memberForm.controls.cdoDay.enable();
		this.memberForm.controls.adjHoursDay.enable();
		this.memberForm.controls.adjHours.enable();

		this.memberForm.get('startDate')?.setValue(this.getDateString(startDate));

		if (this.userConfigurations.length === 0) {
			this.memberForm.get('startDate')?.disable();
		} else {
			this.memberForm.get('startDate')?.enable();
		}

		// this.memberForm.get('employmentStatus')?.setValue(config.employmentStatus);
		// this.memberForm.get('hoursPerPP')?.setValue(config.hoursPerPayPeriod);
		// this.memberForm.get('cdoDay')?.setValue(config.cdoDay);
		// this.memberForm.get('regDayHrs')?.setValue(config.durationOfRegularDay);
		// this.memberForm.get('adjHoursDay')?.setValue(config.modifiedDurationDay);
		// this.memberForm.get('adjHours')?.setValue(config.durationOfModifiedDay);
		// this.memberForm.get('unionCode')?.setValue(config.unionCode);

		const ngbModalOptions: NgbModalOptions = {
			backdrop: 'static',
			keyboard: false,
			centered: true,
			size: 'lg',
			windowClass: 'profileConfigModal',
		};

		this.configModalRef = this.modalService.open(this.configModal, ngbModalOptions);
		this.configModalRef.result.then((shouldSaveChanges: boolean) => {
			if (shouldSaveChanges) {
				const configMember = {
					userId: this.selectedUser.id,
					cdo: Number(this.memberForm.value.cdoDay) > 0,
					cdoDay: Number(this.memberForm.value.cdoDay || 0),
					modifiedDurationDay: Number(this.memberForm.value.adjHoursDay || 0),
					durationOfModifiedDay: Number(this.memberForm.value.adjHours || 0),
					durationOfRegularDay: Number(this.memberForm.value.regDayHrs),
					employmentStatus: this.memberForm.value.employmentStatus,
					hoursPerPayPeriod: Number(this.memberForm.value.hoursPerPP),
					startingDate: HelperFunctions.getBeginningOfDayDate(this.selectedStartDate),
					unionCode: this.memberForm.value.unionCode,
				} as Configuration;

				if (this.isUpdateModal) {
					let updatePayload: UpdateMemberConfig = {
						oldConfig: this.selectedConfiguration,
						newConfig: configMember,
					};

					// console.log(updatePayload);
					this.store.dispatch(UpdateMemberProfile({ updateConfig: updatePayload }));
				} else {
					// console.log(configMember);
					this.store.dispatch(SaveMemberProfile({ config: configMember }));
				}
			}

			// this.memberForm.reset();
			this.resetMemberForm();
		});
	}

	updateEmpStatusAffectedFields() {
		let empStatus = this.memberForm.get('employmentStatus')?.value;
		if (empStatus === 'AUX') {
			if (this.oldCdo === null) this.oldCdo = this.memberForm.get('cdoDay')?.value;
			if (this.oldAdjHrsDay === null) this.oldAdjHrsDay = this.memberForm.get('adjHoursDay')?.value;
			if (this.oldAdjHrs === null) this.oldAdjHrs = this.memberForm.get('adjHours')?.value;

			this.memberForm.get('cdoDay')?.setValue('0');
			this.memberForm.get('adjHoursDay')?.setValue('0');
			this.memberForm.get('adjHours')?.setValue('0');

			this.memberForm.controls.cdoDay.disable();
			this.memberForm.controls.adjHoursDay.disable();
			this.memberForm.controls.adjHours.disable();
		} else {
			if (this.oldCdo !== null) this.memberForm.get('cdoDay')?.setValue(this.oldCdo);
			if (this.oldAdjHrsDay !== null) this.memberForm.get('adjHoursDay')?.setValue(this.oldAdjHrsDay);
			if (this.oldAdjHrs !== null) this.memberForm.get('adjHours')?.setValue(this.oldAdjHrs);

			this.oldCdo = null;
			this.oldAdjHrsDay = null;
			this.oldAdjHrs = null;

			this.memberForm.controls.cdoDay.enable();
			this.memberForm.controls.adjHoursDay.enable();
			this.memberForm.controls.adjHours.enable();

			this.updateCDOAffectedFields();
		}
	}

	updateCDOAffectedFields() {
		let cdoDayValue = this.memberForm.get('cdoDay')?.value;
		if (cdoDayValue == '0') {
			if (this.oldAdjHrsDay === null) this.oldAdjHrsDay = this.memberForm.get('adjHoursDay')?.value;
			if (this.oldAdjHrs === null) this.oldAdjHrs = this.memberForm.get('adjHours')?.value;

			this.memberForm.get('adjHoursDay')?.setValue('0');
			this.memberForm.get('adjHours')?.setValue('0');

			this.memberForm.controls.adjHoursDay.disable();
			this.memberForm.controls.adjHours.disable();
		} else {
			if (this.oldAdjHrsDay !== null) this.memberForm.get('adjHoursDay')?.setValue(this.oldAdjHrsDay);
			if (this.oldAdjHrs !== null) this.memberForm.get('adjHours')?.setValue(this.oldAdjHrs);

			this.oldAdjHrsDay = null;
			this.oldAdjHrs = null;

			this.memberForm.controls.adjHoursDay.enable();
			this.memberForm.controls.adjHours.enable();
		}
	}

	resetMemberForm() {
		this.memberForm.reset();
		this.memberForm.get('startDate')?.setValue('');
		this.memberForm.get('employmentStatus')?.setValue(-1);
		this.memberForm.get('hoursPerPP')?.setValue('');
		this.memberForm.get('cdoDay')?.setValue(-1);
		this.memberForm.get('regDayHrs')?.setValue('');
		this.memberForm.get('adjHoursDay')?.setValue(-1);
		this.memberForm.get('adjHours')?.setValue('');
		this.memberForm.get('unionCode')?.setValue(-1);
	}

	dismissModal(shouldSaveChanges: boolean = false) {
		this.configModalRef.close(shouldSaveChanges);
	}

	getDateString(startDate: string | Date) {
		let startingDate = new Date(startDate);
		const { day, month, date, year } = HelperFunctions.extractDateDetails(startingDate);
		return `${day} ${month} ${date}, ${year}`;
	}

	getDayOfWeek(dayNumber: number) {
		return HelperFunctions.getDayOfWeek(dayNumber);
	}

	getCDODay(config: Configuration) {
		if (!config.cdo) return 'Not Enrolled';
		return this.getWeekAndDay(config.cdoDay);
	}

	getModifiedDurationDay(config: Configuration) {
		if (!config.cdo) return 'N/A';
		return this.getWeekAndDay(config.modifiedDurationDay);
	}

	getWeekAndDay(dayNumber: number) {
		let day = this.getDayOfWeek(dayNumber);
		let weekNumber = dayNumber <= 7 ? 1 : 2;

		return `Week ${weekNumber} ${day}`;
	}

	getModifiedDurationHrs(config: Configuration) {
		if (!config.cdo) return 'N/A';
		return config.durationOfModifiedDay;
	}

	getEmployeeType(empType: string) {
		let type = empType;
		if (empType === 'ExemptManagement') type = 'Exempt Management';
		if (empType === 'ExemptSupport') type = 'Exempt Support';
		return type;
	}

	onDateSelect(date: NgbDate, formControl: string) {
		this.selectedStartDate = new Date(date.year, date.month - 1, date.day);
		this.ngbStartDate = date;
		this.memberForm.controls[formControl].setValue(this.getDateString(this.selectedStartDate));
	}

	isSelectedDate(date: NgbDate, formControl: string) {
		let startDate = this.ngbStartDate;

		if (startDate !== null) {
			let selectedDate = new Date(startDate.year, startDate.month - 1, startDate.day);
			return selectedDate.getDate() === date.day && selectedDate.getMonth() + 1 === date.month && selectedDate.getFullYear() === date.year;
		}

		return false;
	}
}
